import { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Modal, Radio, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { PaycloudBranch } from "../../types";
import { useToast } from "../../context/toast-context";
import { CloverService, AvailableCloverBranch } from "../../services/clover-service";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "600px",
  maxWidth: "90vw",
  maxHeight: "80vh",
  overflowY: "auto",
  bgcolor: "#fff",
  border: "1px solid #898989",
  boxShadow: 24,
  borderRadius: 4,
};

interface CloverReassignBranchModalProps {
  isOpen: boolean;
  onClose: () => void;
  branch: PaycloudBranch;
  onReassigned: () => void;
}

export function CloverReassignBranchModal({
  isOpen,
  onClose,
  branch,
  onReassigned,
}: CloverReassignBranchModalProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const cloverService = useMemo(() => new CloverService(), []);

  const [availableBranches, setAvailableBranches] = useState<AvailableCloverBranch[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !branch.cloverExternalId) {
      return;
    }

    const fetchBranches = async () => {
      setIsLoading(true);
      setSelectedBranchId(null);
      try {
        await cloverService.getAvailableBranches(
          branch.cloverExternalId!,
          (data) => {
            setAvailableBranches(data.branches);
          },
          (errorMessage) => {
            showToast(errorMessage, "error");
            onClose();
          }
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchBranches();
  }, [isOpen, branch.cloverExternalId, cloverService, onClose, showToast]);

  const handleSave = async () => {
    if (!branch.cloverExternalId || selectedBranchId === null) {
      return;
    }

    setIsSaving(true);
    try {
      await cloverService.reassignBranch(
        branch.cloverExternalId,
        selectedBranchId,
        () => {
          showToast(t("branches.connection.reassign-success"), "success");
          onReassigned();
          onClose();
        },
        (errorMessage) => {
          showToast(errorMessage, "error");
        }
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="clover-reassign-branch-modal"
    >
      <Box sx={style}>
        <div className="bg-gray-light text-center text-xl font-normal text-black py-6 px-2">
          <h1>{t("branches.connection.reassign-title")}</h1>
        </div>
        <div className="p-6">
          <p className="text-gray-600 text-[15px] leading-7 mb-4">
            {t("branches.connection.reassign-description", { branchName: branch.name })}
          </p>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <CircularProgress />
            </div>
          ) : availableBranches.length === 0 ? (
            <Typography color="textSecondary" className="py-4 text-center">
              {t("branches.connection.reassign-empty")}
            </Typography>
          ) : (
            <div className="space-y-3 mb-6">
              {availableBranches.map((availableBranch) => (
                <label
                  key={availableBranch.branchId}
                  className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50"
                >
                  <Radio
                    checked={selectedBranchId === availableBranch.branchId}
                    onChange={() => setSelectedBranchId(availableBranch.branchId)}
                    value={availableBranch.branchId}
                  />
                  <div>
                    <Typography variant="subtitle1">{availableBranch.name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {availableBranch.address}
                    </Typography>
                  </div>
                </label>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="outlined" onClick={onClose} disabled={isSaving}>
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving || selectedBranchId === null || availableBranches.length === 0}
              sx={{
                backgroundColor: "#EF0BB8",
                "&:hover": {
                  backgroundColor: "#d009a6",
                },
              }}
            >
              {isSaving ? <CircularProgress size={24} color="inherit" /> : t("save")}
            </Button>
          </div>
        </div>
      </Box>
    </Modal>
  );
}
